/**
 * Zustand Store for M-Scale Manager
 * Centralized state management
 */

import { create } from 'zustand'
import { DeviceLinkCommand, type PairInfo, type HardwareConfig, type SystemStatus, type TargetRange, type DeviceLinkStatus, type DeviceLinkConfig, type PositionMapEntry } from '../services/bluetooth/types'
import type { OTAStatus } from '../services/bluetooth/OTAService'
import { bluetoothService } from '../services/bluetooth/BluetoothService'
import { calculateVirtualGrid } from '../utils/virtualGrid'

/**
 * Helper: Save layout config to localStorage
 */
function saveLayoutConfigToLocalStorage(config: DeviceLinkConfig): void {
  try {
    localStorage.setItem('mscale_layout_config', JSON.stringify(config))
  } catch (error) {
    console.error('Failed to save layout config to localStorage:', error)
  }
}

/**
 * Helper: Load layout config from localStorage
 */
function loadLayoutConfigFromLocalStorage(): DeviceLinkConfig | null {
  try {
    const saved = localStorage.getItem('mscale_layout_config')
    return saved ? JSON.parse(saved) : null
  } catch (error) {
    console.error('Failed to load layout config from localStorage:', error)
    return null
  }
}

/**
 * Helper: Clear layout config from localStorage
 */
function clearLayoutConfigFromLocalStorage(): void {
  try {
    localStorage.removeItem('mscale_layout_config')
  } catch (error) {
    console.error('Failed to clear layout config from localStorage:', error)
  }
}

/**
 * Helper: Save display settings to localStorage
 */
function saveDisplaySettingsToLocalStorage(showHundredthsUnder10g: boolean): void {
  try {
    localStorage.setItem('mscale_show_hundredths', JSON.stringify(showHundredthsUnder10g))
  } catch (error) {
    console.error('Failed to save display settings to localStorage:', error)
  }
}

/**
 * Helper: Load display settings from localStorage
 */
function loadDisplaySettingsFromLocalStorage(): boolean {
  try {
    const saved = localStorage.getItem('mscale_show_hundredths')
    return saved ? JSON.parse(saved) : false
  } catch (error) {
    console.error('Failed to load display settings from localStorage:', error)
    return false
  }
}

interface MScaleState {
  // Connection
  isConnected: boolean
  deviceName: string | null
  firmwareVersion: string | null
  isConnecting: boolean
  connectionError: string | null
  connectionProgress: { step: string; current: number; total: number } | null

  // Hardware Config
  hardwareConfig: HardwareConfig | null

  // Weights
  weights: number[]
  lastWeightUpdate: number | null

  // Pairing
  groups: PairInfo[]
  groupCount: number
  lastGroupUpdate: number | null

  // Target Range
  targetRange: TargetRange

  // Group Size
  groupSize: number

  // Minimum Ratio
  minRatio: number

  // System Status
  systemStatus: SystemStatus | null

  // OTA Status
  otaStatus: OTAStatus | null

  // Dual-Device Mode
  isDualDeviceMode: boolean
  deviceLinkStatus: DeviceLinkStatus | null
  secondaryDevice: { id: string; name: string } | null
  layoutConfig: DeviceLinkConfig
  virtualGrid: {
    rows: number
    cols: number
    scalePositions: PositionMapEntry[]
  }

  // UI State
  selectedPosition: number | null
  showSettings: boolean
  showCalibration: boolean
  showDiagnostics: boolean

  // Display Settings (browser-local)
  showHundredthsUnder10g: boolean

  // Actions
  setConnected: (connected: boolean, deviceName?: string) => void
  setConnecting: (connecting: boolean) => void
  setConnectionError: (error: string | null) => void
  setConnectionProgress: (progress: { step: string; current: number; total: number } | null) => void
  setDeviceInfo: (name: string, version: string, config: HardwareConfig) => void
  setWeights: (weights: number[]) => void
  setGroups: (groups: PairInfo[], count: number) => void
  setTargetRange: (range: TargetRange) => void
  setGroupSize: (size: number) => void
  setMinRatio: (ratio: number) => void
  setSystemStatus: (status: SystemStatus) => void
  setOTAStatus: (status: OTAStatus) => void
  setSelectedPosition: (position: number | null) => void
  setShowSettings: (show: boolean) => void
  setShowCalibration: (show: boolean) => void
  setShowDiagnostics: (show: boolean) => void
  setShowHundredthsUnder10g: (show: boolean) => void

  // Dual-Device Actions
  setDualDeviceMode: (enabled: boolean) => void
  setDeviceLinkStatus: (status: DeviceLinkStatus | null) => void
  setSecondaryDevice: (device: { id: string; name: string } | null) => void
  setLayoutConfig: (config: DeviceLinkConfig) => void
  updateVirtualGrid: () => void
  linkSecondaryDevice: (deviceId: string, deviceName: string) => Promise<void>
  unlinkSecondaryDevice: () => Promise<void>

  reset: () => void
}

// Load saved settings from localStorage
const savedLayoutConfig = loadLayoutConfigFromLocalStorage()
const savedShowHundredths = loadDisplaySettingsFromLocalStorage()

const initialState = {
  isConnected: false,
  deviceName: null,
  firmwareVersion: null,
  isConnecting: false,
  connectionError: null,
  connectionProgress: null,
  hardwareConfig: null,
  weights: Array(32).fill(0),
  lastWeightUpdate: null,
  groups: [],
  groupCount: 0,
  lastGroupUpdate: null,
  targetRange: { min: 28.0, max: 32.0 },
  groupSize: 2,
  minRatio: 0.5,
  systemStatus: null,
  otaStatus: null,
  isDualDeviceMode: false,
  deviceLinkStatus: null,
  secondaryDevice: null,
  layoutConfig: savedLayoutConfig || {
    relativePosition: 'right' as const,
    primaryRotation: 0 as const,
    secondaryRotation: 0 as const,
  },
  virtualGrid: {
    rows: 4,
    cols: 4,
    scalePositions: [],
  },
  selectedPosition: null,
  showSettings: false,
  showCalibration: false,
  showDiagnostics: false,
  showHundredthsUnder10g: savedShowHundredths,
}

export const useMScaleStore = create<MScaleState>((set) => ({
  ...initialState,

  setConnected: (connected, deviceName) =>
    set((state) => ({
      isConnected: connected,
      deviceName: deviceName ?? null,
      // Only set isConnecting to false if we're disconnecting
      // When connecting, keep isConnecting true until connection is fully complete
      isConnecting: connected ? state.isConnecting : false,
      connectionError: null
    })),

  setConnecting: (connecting) =>
    set({ isConnecting: connecting, connectionError: null, connectionProgress: null }),

  setConnectionError: (error) =>
    set({ connectionError: error, isConnecting: false, connectionProgress: null }),

  setConnectionProgress: (progress) =>
    set((state) => ({
      connectionProgress: progress,
      // Ensure isConnecting stays true if we're updating progress
      isConnecting: progress ? true : state.isConnecting
    })),

  setDeviceInfo: (name, version, config) =>
    set({
      deviceName: name,
      firmwareVersion: version,
      hardwareConfig: config
    }),

  setWeights: (weights) =>
    set({ weights, lastWeightUpdate: Date.now() }),

  setGroups: (groups, count) =>
    set({ groups, groupCount: count, lastGroupUpdate: Date.now() }),

  setTargetRange: (range) =>
    set({ targetRange: range }),

  setGroupSize: (size) =>
    set({ groupSize: size }),

  setMinRatio: (ratio) =>
    set({ minRatio: ratio }),

  setSystemStatus: (status) =>
    set({ systemStatus: status }),

  setOTAStatus: (status) =>
    set({ otaStatus: status }),

  setSelectedPosition: (position) =>
    set({ selectedPosition: position }),

  setShowSettings: (show) =>
    set({ showSettings: show }),

  setShowCalibration: (show) =>
    set({ showCalibration: show }),

  setShowDiagnostics: (show) =>
    set({ showDiagnostics: show }),

  setShowHundredthsUnder10g: (show) => {
    saveDisplaySettingsToLocalStorage(show)
    set({ showHundredthsUnder10g: show })
  },

  // Dual-Device Actions
  setDualDeviceMode: (enabled) =>
    set({ isDualDeviceMode: enabled }),

  setDeviceLinkStatus: (status) =>
    set((state) => {
      // Activate dual-device mode if device is linked
      const isDualDeviceMode = status?.linkActive === true

      // Update virtual grid when link status changes
      const virtualGrid = isDualDeviceMode
        ? calculateVirtualGrid(state.layoutConfig, state.hardwareConfig)
        : state.virtualGrid

      return {
        deviceLinkStatus: status,
        isDualDeviceMode,
        virtualGrid,
        // Set secondary device info if available
        secondaryDevice: status?.linkActive && status.linkedDeviceName
          ? { id: 'linked-device', name: status.linkedDeviceName }
          : null
      }
    }),

  setSecondaryDevice: (device) =>
    set({ secondaryDevice: device }),

  setLayoutConfig: (config) =>
    set((state) => {
      const newState = { layoutConfig: config }
      // Recalculate virtual grid when layout changes
      const grid = calculateVirtualGrid(config, state.hardwareConfig)
      return { ...newState, virtualGrid: grid }
    }),

  updateVirtualGrid: () =>
    set((state) => ({
      virtualGrid: calculateVirtualGrid(state.layoutConfig, state.hardwareConfig)
    })),

  linkSecondaryDevice: async (deviceId, deviceName) => {
    try {
      // Store secondary device info
      set({ secondaryDevice: { id: deviceId, name: deviceName } })

      // Send link command to primary device
      await bluetoothService.sendLinkCommand(DeviceLinkCommand.CONNECT_TO_SECONDARY, deviceId)

      // Subscribe to link status updates
      await bluetoothService.subscribeToDeviceLinkStatus((status) => {
        set({ deviceLinkStatus: status, isDualDeviceMode: status.linkActive })
      })

      // Read initial link status
      const status = await bluetoothService.getDeviceLinkStatus()
      set({ deviceLinkStatus: status, isDualDeviceMode: status.linkActive })

      // Read current layout config from device
      const config = await bluetoothService.getDeviceLinkConfig()
      set((state) => ({
        layoutConfig: config,
        virtualGrid: calculateVirtualGrid(config, state.hardwareConfig)
      }))

      // Save to localStorage for persistence
      saveLayoutConfigToLocalStorage(useMScaleStore.getState().layoutConfig)
    } catch (error) {
      console.error('Failed to link secondary device:', error)
      set({ secondaryDevice: null })
      throw error
    }
  },

  unlinkSecondaryDevice: async () => {
    try {
      // Send disconnect command to primary device
      await bluetoothService.sendLinkCommand(DeviceLinkCommand.DISCONNECT)

      // Unsubscribe from link status updates
      await bluetoothService.unsubscribeFromDeviceLinkStatus()

      // Reset dual-device state
      set({
        isDualDeviceMode: false,
        deviceLinkStatus: null,
        secondaryDevice: null,
        layoutConfig: {
          relativePosition: 'right',
          primaryRotation: 0,
          secondaryRotation: 0,
        },
        virtualGrid: {
          rows: 4,
          cols: 4,
          scalePositions: [],
        },
      })

      // Clear from localStorage
      clearLayoutConfigFromLocalStorage()
    } catch (error) {
      console.error('Failed to unlink secondary device:', error)
      throw error
    }
  },

  reset: () =>
    set(initialState),
}))
